import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth.jsx';
import { ADMIN_ROLE_SLUGS } from '../common/helpers/roleLabels';
import { Result, Spin } from '@arco-design/web-react';

const ProtectedRoute = ({
  children,
  requiredRole = null,
  requiredRoles = null,
  requiredPermission = null,
  redirectTo = '/login',
  unauthorizedRedirectTo = '/dashboard',
  redirectDelay = 3000,
  requireOnboardingComplete = true
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const { isAuthenticated, loading, hasRole, hasPermission, user, logout } = useAuth();
  const [countdown, setCountdown] = useState(3);
  const isOnboardingExempt = user?.active_profile?.role?.name
    ? ADMIN_ROLE_SLUGS.includes(user.active_profile.role.name)
    : user?.profiles?.some(profile => profile.is_active && ADMIN_ROLE_SLUGS.includes(profile.role?.name)) || false;
  const needsOnboarding =
    user?.registration_status === 'PRE_REGISTRADO' ||
    user?.registration_status === 'REGISTRO_EN_REVISION';

  useEffect(() => {
    if (requireOnboardingComplete && needsOnboarding && !isOnboardingExempt) {
      logout();
    }
  }, [requireOnboardingComplete, needsOnboarding, isOnboardingExempt, logout]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size={40} />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (requireOnboardingComplete && needsOnboarding && !isOnboardingExempt) {
    return <Navigate to={redirectTo} replace />;
  }

  const allowOnboardingEdit = location.state?.allowOnboardingEdit
    || new URLSearchParams(location.search).get('allow_edit') === '1';

  if (!requireOnboardingComplete && location.pathname === '/onboarding/wizard' && user) {
    const hasSubmittedOrCompleted =
      user.registration_status === 'REGISTRO_EN_REVISION' ||
      user.registration_status === 'APROBADO' ||
      user.registration_status === 'RECHAZADO';

    if (hasSubmittedOrCompleted && !allowOnboardingEdit) {
      return <Navigate to="/onboarding/status" replace />;
    }
  }

  const hasRequiredRole = () => {
    if (requiredRole && !hasRole(requiredRole)) return false;
    if (requiredRoles && !requiredRoles.some(role => hasRole(role))) return false;
    return true;
  };

  const hasRequiredPermission = () => {
    if (requiredPermission && !hasPermission(requiredPermission)) return false;
    return true;
  };

  const AccessDenied = ({ message, detail }) => {
    useEffect(() => {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate(unauthorizedRedirectTo, { replace: true });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }, []);

    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', padding: 20 }}>
        <Result
          status="403"
          title={t('accessDenied.title')}
          subTitle={
            <div>
              <p>{message}</p>
              {detail && <p style={{ color: '#86909C', fontSize: '14px' }}>{detail}</p>}
              <p style={{ marginTop: 16 }}>
                {t('accessDenied.redirecting', { count: countdown })}
              </p>
            </div>
          }
        />
      </div>
    );
  };

  if (!hasRequiredRole()) {
    const detail = requiredRole
      ? t('accessDenied.requiredRole', { role: requiredRole })
      : t('accessDenied.allowedRoles', { roles: requiredRoles.join(', ') });

    return <AccessDenied
      message={t('accessDenied.noPermission')}
      detail={detail}
    />;
  }

  if (!hasRequiredPermission()) {
    return <AccessDenied
      message={t('accessDenied.noPermission')}
      detail={t('accessDenied.requiredPermission', { permission: requiredPermission })}
    />;
  }

  return children;
};

export default ProtectedRoute;
